import { useEffect, useMemo, useRef, useState } from "react";
import {
  catalogSlug,
  defaultLanguage,
  filterCatalogItems,
  formatPrice,
  localized,
  updateCatalogHeadMetadata,
} from "./catalog";
import { catalogImageUrl, loadCatalog, subscribeToCatalog } from "./data";
import type { CatalogItem, CatalogLanguage, CatalogSnapshotV1 } from "./types";
import { startLiveCatalog, type LiveCatalogState } from "./live-catalog";

type LoadState = LiveCatalogState<CatalogSnapshotV1>;

function CatalogImage({
  fileId,
  alt,
  large = false,
}: {
  fileId?: string;
  alt: string;
  large?: boolean;
}) {
  const [broken, setBroken] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setBroken(false);
    setLoaded(false);
  }, [fileId]);

  if (!fileId || broken) {
    const initial = alt.trim().charAt(0).toUpperCase() || "S";
    return (
      <div className="image-placeholder" aria-label={alt}>
        <span>{initial}</span>
      </div>
    );
  }

  return (
    <div className={`image-wrapper ${loaded ? "is-loaded" : "is-loading"}`}>
      {!loaded && <div className="shimmer-skeleton" aria-hidden="true" />}
      <img
        className="catalog-image"
        src={catalogImageUrl(fileId, large ? 1200 : 640)}
        alt={alt}
        loading={large ? "eager" : "lazy"}
        onLoad={() => setLoaded(true)}
        onError={() => setBroken(true)}
      />
    </div>
  );
}

function ItemDetail({
  item,
  language,
  onClose,
}: {
  item: CatalogItem;
  language: CatalogLanguage;
  onClose: () => void;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const name = localized(item.name, item.nameAr, language);
  const description = localized(item.description, item.descriptionAr, language);

  useEffect(() => {
    closeRef.current?.focus();
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
      if (event.key === "Tab") {
        event.preventDefault();
        closeRef.current?.focus();
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  return (
    <div className="dialog-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="detail-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="detail-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <button
          ref={closeRef}
          className="close-button"
          type="button"
          onClick={onClose}
          aria-label={language === "ar" ? "إغلاق" : "Close"}
        >
          ×
        </button>
        <div className="detail-image">
          <CatalogImage fileId={item.image?.fileId} alt={name} large />
        </div>
        <div className="detail-copy">
          {item.availability === "sold_out" && (
            <span className="sold-out">
              {language === "ar" ? "غير متوفر حالياً" : "Currently unavailable"}
            </span>
          )}
          <h2 id="detail-title">{name}</h2>
          {description && <p>{description}</p>}
          <strong className="detail-price">
            {formatPrice(item.price, language)}
          </strong>
          {item.variants && item.variants.length > 0 && (
            <div className="variants">
              <h3>{language === "ar" ? "الخيارات" : "Options"}</h3>
              {item.variants.map((variant) => (
                <div className="variant" key={variant.id}>
                  <span>
                    {localized(variant.label, variant.labelAr, language)}
                  </span>
                  <span>{formatPrice(variant.price, language)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

/**
 * The organisation's palette.
 *
 * Every palette ships in this bundle, so the snapshot carries only an id. An
 * absent or unrecognised one leaves the default -- which is what a catalog
 * published before the theme engine renders on, with no republish needed.
 *
 * Set on <html> rather than on the container because body's background reads
 * these tokens too, and would otherwise stay on the default palette behind a
 * rubber-band overscroll.
 */
function useOrgTheme(themeId: string | undefined) {
  useEffect(() => {
    if (!themeId || !/^[a-z0-9-]{1,32}$/.test(themeId)) return;
    document.documentElement.dataset.theme = themeId;
    return () => {
      delete document.documentElement.dataset.theme;
    };
  }, [themeId]);
}

export default function App() {
  const slug = catalogSlug(window.location.pathname);
  const [state, setState] = useState<LoadState>({ status: "loading" });
  const [language, setLanguage] = useState<CatalogLanguage>(() =>
    defaultLanguage(navigator.languages),
  );
  const [search, setSearch] = useState("");
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<CatalogItem | null>(null);

  useEffect(() => {
    if (!slug) {
      setState({ status: "missing" });
      return;
    }
    // A realtime failure no longer replaces a catalog that loaded (WID-12).
    return startLiveCatalog({
      load: () => loadCatalog(slug),
      subscribe: (onSnapshot, onDelete) =>
        subscribeToCatalog(slug, onSnapshot, onDelete),
      onState: setState,
    });
  }, [slug]);

  const snapshot = state.status === "ready" ? state.snapshot : null;

  useEffect(() => {
    if (snapshot) {
      updateCatalogHeadMetadata(snapshot, language, catalogImageUrl);
    }
  }, [snapshot, language]);

  const visibleItems = useMemo(
    () =>
      snapshot
        ? filterCatalogItems({
            items: snapshot.items,
            categories: snapshot.categories,
            language,
            search,
            categoryId,
          })
        : [],
    [categoryId, language, search, snapshot],
  );

  useOrgTheme(snapshot?.branding.themeId);

  useEffect(() => {
    if (!snapshot) return;
    setSelectedItem((current) =>
      current
        ? (snapshot.items.find((item) => item.id === current.id) ?? null)
        : null,
    );
    if (
      categoryId &&
      !snapshot.categories.some((category) => category.id === categoryId)
    ) {
      setCategoryId(null);
    }
  }, [categoryId, snapshot]);

  if (state.status === "loading") {
    return (
      <main className="state-page">
        <div className="loader" />
        <p>Loading catalog…</p>
      </main>
    );
  }
  if (state.status === "missing") {
    return (
      <main className="state-page">
        <div className="state-mark">404</div>
        <h1>Catalog not found</h1>
        <p>This link may be unpublished or incorrect.</p>
      </main>
    );
  }
  if (state.status === "error" || !snapshot) {
    return (
      <main className="state-page">
        <div className="state-mark">!</div>
        <h1>Catalog unavailable</h1>
        <p>Please try again in a moment.</p>
        <button type="button" onClick={() => window.location.reload()}>
          Try again
        </button>
      </main>
    );
  }

  const title = localized(
    snapshot.branding.title,
    snapshot.branding.titleAr,
    language,
  );
  const emptyMessage =
    search || categoryId
      ? language === "ar"
        ? "لا توجد نتائج مطابقة لفلتر البحث."
        : "No items match your search or filter."
      : language === "ar"
        ? "لا توجد عناصر منشورة بعد."
        : "No items have been published yet.";

  return (
    <div
      className="catalog"
      dir={language === "ar" ? "rtl" : "ltr"}
      // The organisation's accent. Renamed from --accent, which collided with
      // the canonical token of that name (a neutral surface, not a brand).
      style={{ "--brand": snapshot.branding.accentColor } as React.CSSProperties}
    >
      <header className="hero">
        <div className="brand">
          <div className="logo">
            <CatalogImage fileId={snapshot.branding.logo?.fileId} alt={title} />
          </div>
          <div>
            <span className="eyebrow">
              {snapshot.kind === "restaurant"
                ? language === "ar"
                  ? "قائمة الطعام"
                  : "Menu"
                : language === "ar"
                  ? "المنتجات"
                  : "Catalog"}
            </span>
            <h1>{title}</h1>
          </div>
        </div>
        <button
          type="button"
          className="language-button"
          onClick={() =>
            setLanguage((current) => (current === "ar" ? "en" : "ar"))
          }
        >
          {language === "ar" ? "English" : "العربية"}
        </button>
      </header>

      <main className="content">
        <div className="toolbar">
          <label className="search">
            <span className="sr-only">
              {language === "ar" ? "بحث" : "Search"}
            </span>
            <span aria-hidden className="search-icon">⌕</span>
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={
                language === "ar" ? "ابحث في القائمة..." : "Search the catalog..."
              }
            />
            {search && (
              <button
                type="button"
                className="search-clear-btn"
                onClick={() => setSearch("")}
                aria-label={language === "ar" ? "مسح النص" : "Clear search"}
              >
                ✕
              </button>
            )}
          </label>
          {snapshot.categories.length > 0 && (
            <nav
              className="chips"
              aria-label={language === "ar" ? "التصنيفات" : "Categories"}
            >
              <button
                className={!categoryId ? "active" : ""}
                type="button"
                onClick={() => setCategoryId(null)}
              >
                {language === "ar" ? "الكل" : "All"}
              </button>
              {snapshot.categories.map((category) => (
                <button
                  className={categoryId === category.id ? "active" : ""}
                  type="button"
                  key={category.id}
                  onClick={() => setCategoryId(category.id)}
                >
                  {localized(category.name, category.nameAr, language)}
                </button>
              ))}
            </nav>
          )}
        </div>

        {visibleItems.length === 0 ? (
          <div className="empty">
            <span className="empty-mark">◇</span>
            <p>{emptyMessage}</p>
            {(search || categoryId) && (
              <button
                type="button"
                className="reset-button"
                onClick={() => {
                  setSearch("");
                  setCategoryId(null);
                }}
              >
                {language === "ar" ? "إعادة ضبط البحث" : "Reset filters"}
              </button>
            )}
          </div>
        ) : (
          <section className="grid" aria-live="polite">
            {visibleItems.map((item) => {
              const name = localized(item.name, item.nameAr, language);
              const description = localized(
                item.description,
                item.descriptionAr,
                language,
              );
              return (
                <button
                  className="item-card"
                  type="button"
                  key={item.id}
                  onClick={() => setSelectedItem(item)}
                >
                  <div className="item-image">
                    <CatalogImage fileId={item.image?.fileId} alt={name} />
                    {item.availability === "sold_out" && (
                      <span className="sold-out">
                        {language === "ar" ? "نفد" : "Sold out"}
                      </span>
                    )}
                  </div>
                  <div className="item-copy">
                    <h2>{name}</h2>
                    {description && <p>{description}</p>}
                    <strong className="item-price">{formatPrice(item.price, language)}</strong>
                  </div>
                </button>
              );
            })}
          </section>
        )}
      </main>

      <footer>
        {language === "ar" ? "كتالوج بواسطة" : "Catalog by"}{" "}
        <strong>Sela</strong>
      </footer>
      {selectedItem && (
        <ItemDetail
          item={selectedItem}
          language={language}
          onClose={() => setSelectedItem(null)}
        />
      )}
    </div>
  );
}

